import { Link } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ChevronLeftIcon } from "@heroicons/react/24/outline";
import { api, ApiError } from "../lib/api";
import { useToast } from "../components/Toast";
import { SignedAmount } from "../components/SignedAmount";
import { formatDate } from "../lib/format";

/**
 * Abgleich Buchung ↔ Beleg (Block 3c, 18.10.2026). Vorschläge: gleicher Betrag im Zeitfenster aus den Einstellungen; der Händlername
 * ordnet nur. „Alle eindeutigen übernehmen“ verknüpft nur Paare, die in BEIDE Richtungen eindeutig sind. Verknüpfen trägt „Bezahlt am“
 * am Beleg ein (nur wenn leer und nie von Hand gesetzt) und hebt eine Bestätigung der Buchung auf (neu prüfen).
 */
type BuchungK = { id: string; datum: string; betrag_cent: number; text: string | null; gegenpartei: string | null; konto: string };
type Vorschlag = {
  beleg: { id: string; vendor: string | null; gesamt: number; receipt_date: number; payment_method: string | null };
  eindeutig: boolean;
  kandidaten: { buchungId: string; abstandTage: number; haendler: boolean; buchung: BuchungK }[];
};
type Antwort = {
  vorschlaege: Vorschlag[]; eindeutig: number; ohneKandidat: number;
  barbelege: { neu: number; mitKandidat: number } | { fehler: string };
  offeneRechnungen: { receiptId: string; vendor: string | null; betrag: number; bezug: string; geprueft: boolean; kandidaten: unknown[] }[];
};
const fehlertext = (e: unknown) => (e instanceof ApiError ? e.message : "Aktion fehlgeschlagen");

export function AbgleichPage() {
  const qc = useQueryClient();
  const toast = useToast();
  const { data, isLoading } = useQuery<Antwort>({ queryKey: ["abgleich"], queryFn: () => api.get("/api/abgleich") });
  const neuLaden = () => ["abgleich", "buchungen", "konten", "buchungen-jahre"].forEach((k) => void qc.invalidateQueries({ queryKey: [k] }));
  const eindeutige = useMutation({
    mutationFn: () => api.post<{ verknuepft: number; bezahltAm: number }>("/api/abgleich/eindeutige"),
    onSuccess: (r) => { toast.success(`${r.verknuepft} verknüpft, „Bezahlt am“ bei ${r.bezahltAm} Belegen eingetragen`); neuLaden(); },
    onError: (e) => toast.error(fehlertext(e)),
  });
  const verknuepfen = useMutation({
    mutationFn: (p: { buchungId: string; receiptId: string }) => api.post("/api/abgleich/verknuepfen", p),
    onSuccess: () => { toast.success("Verknüpft — Buchung bitte prüfen und bestätigen"); neuLaden(); }, onError: (e) => toast.error(fehlertext(e)),
  });
  const bar = useMutation({
    mutationFn: () => api.post<{ neu: number }>("/api/abgleich/barbelege?ausfuehren=1"),
    onSuccess: (r) => { toast.success(`${r.neu} Bar-Buchungen angelegt (unbestätigt)`); neuLaden(); }, onError: (e) => toast.error(fehlertext(e)),
  });

  return (
    <div className="max-w-3xl mx-auto p-4 lg:p-6">
      <Link to="/buchungen" className="inline-flex items-center gap-1 text-ios-blue text-[14px] mb-3"><ChevronLeftIcon className="w-4 h-4" /> Buchungen</Link>
      <h1 className="text-[22px] font-semibold tracking-tight mb-1">Abgleich Buchung ↔ Beleg</h1>
      <p className="text-[13px] text-ios-muted mb-4">
        Geprüfte Belege ohne Buchung und passende Buchungen (gleicher Betrag, Datum im Zeitfenster). Ein verknüpfter Beleg liefert die Aufteilung
        auf Buchungsarten, der Betrag kommt von der Buchung. Nach dem Verknüpfen die Buchung unter „Buchungen“ bestätigen.
      </p>
      {isLoading || !data ? <p className="text-[14px] text-ios-muted">Lädt …</p> : (
        <div className="space-y-4">
          <div className="bg-white rounded-iosLg shadow-card p-4 text-[14px] space-y-2">
            <div>{data.vorschlaege.length} Belege mit Vorschlag · {data.ohneKandidat} geprüfte Belege ohne passende Buchung</div>
            <button type="button" disabled={data.eindeutig === 0 || eindeutige.isPending} onClick={() => eindeutige.mutate()}
              className="px-3 py-1.5 rounded-ios bg-ios-blue text-white disabled:opacity-40">alle eindeutigen übernehmen ({data.eindeutig})</button>
          </div>
          <section className="bg-white rounded-iosLg shadow-card p-4 text-[14px] space-y-2">
            <h2 className="font-medium">Barbelege</h2>
            {"fehler" in data.barbelege ? <p className="text-ios-blue">{data.barbelege.fehler}</p> : (
              <>
                <p className="text-[13px] text-ios-muted">Barbelege ohne passende Buchung werden als neue (unbestätigte) Buchung auf dem Bar-Konto angelegt.
                  {data.barbelege.mitKandidat > 0 && ` ${data.barbelege.mitKandidat} Barbelege haben schon einen Vorschlag und werden nicht angelegt.`}</p>
                <button type="button" disabled={data.barbelege.neu === 0 || bar.isPending} onClick={() => bar.mutate()}
                  className="px-3 py-1.5 rounded-ios bg-white shadow-card text-ios-blue disabled:opacity-40">{data.barbelege.neu} Bar-Buchungen anlegen</button>
              </>
            )}
          </section>
          {data.offeneRechnungen.length > 0 && (
            <section className="bg-white rounded-iosLg shadow-card p-4 text-[14px]">
              <h2 className="font-medium mb-1">Offene Rechnungen ({data.offeneRechnungen.length})</h2>
              {data.offeneRechnungen.map((r) => (
                <div key={r.receiptId} className="flex gap-2 py-1">
                  <Link to={`/receipts/${r.receiptId}`} className="flex-1 text-ios-blue truncate">{r.vendor ?? "—"} · {formatDate(r.bezug)}</Link>
                  <SignedAmount value={-r.betrag} treatNegativeAsIncome={false} />
                  <span className="text-[12px] text-ios-muted w-28 text-right">{!r.geprueft ? "erst prüfen" : r.kandidaten.length ? `${r.kandidaten.length} Zahlung(en) passen` : "noch nicht bezahlt"}</span>
                </div>
              ))}
            </section>
          )}
          {data.vorschlaege.map((v) => (
            <div key={v.beleg.id} className="bg-white rounded-iosLg shadow-card p-4 text-[14px]">
              <div className="flex items-baseline gap-2 mb-2">
                <Link to={`/receipts/${v.beleg.id}`} className="flex-1 font-medium text-ios-blue truncate">{v.beleg.vendor ?? "—"}</Link>
                <span className="text-[13px] text-ios-muted">{formatDate(new Date(v.beleg.receipt_date * 1000).toISOString())}</span>
                <SignedAmount value={-v.beleg.gesamt} treatNegativeAsIncome={false} />
                {v.eindeutig && <span className="text-[12px] px-2 py-0.5 rounded-full bg-ios-blue/10 text-ios-blue">eindeutig</span>}
              </div>
              {v.kandidaten.map((k) => (
                <div key={k.buchungId} className="flex items-center gap-2 py-1 border-t border-ios-line">
                  <span className="text-[13px] text-ios-muted w-20 shrink-0">{formatDate(k.buchung.datum)}</span>
                  <span className="flex-1 truncate">{k.buchung.gegenpartei || k.buchung.text || "—"} <span className="text-ios-muted">· {k.buchung.konto}</span></span>
                  <span className="text-[12px] text-ios-muted">{k.abstandTage >= 0 ? `+${k.abstandTage}` : k.abstandTage} Tg{k.haendler ? " · Händler passt" : ""}</span>
                  <button type="button" disabled={verknuepfen.isPending} onClick={() => verknuepfen.mutate({ buchungId: k.buchungId, receiptId: v.beleg.id })}
                    className="px-2 py-1 rounded-ios bg-ios-blue text-white text-[13px]">verknüpfen</button>
                </div>
              ))}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
